import { FC, useState } from 'react'
import {
  Text,
  InputSelectControlField,
  SimpleModal,
  Button,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  useNotifications,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import {
  isSequenceApiError,
  isQuickBooksErrorResponse,
  useMutation,
  useQuery
} from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { RefreshAccountsIcon } from 'Integrations/integrationsConfig/quickBooks/RefreshAccountsIcon'

interface Props {
  onConfirm?: () => void
  productId: string
  onClose: () => void
}

const parseMutationError = (error: unknown) => {
  if (!isSequenceApiError(error)) {
    return {
      isValidationError: false,
      message: error instanceof Error ? error.message : 'Unknown error'
    }
  }

  if (isQuickBooksErrorResponse(error.response)) {
    return {
      isValidationError: error.response?.Fault.type === 'ValidationFault',
      message: error.response?.Fault.Error[0].Message
    }
  }
}

export const LinkQuickbooksItemModal: FC<Props> = ({
  onConfirm,
  productId,
  onClose
}) => {
  const [isCreating, setIsCreating] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { displayNotification } = useNotifications()
  const syncQuickbooksAccountsMutator = useMutation(
    dashboard20240730Client.postSyncQuickbooksAccounts
  )

  const { data, isLoading } = useQuery(
    dashboardv99990101Client.getQuickbooksAccounts
  )

  const quickbooksAccounts = data?.items ?? []
  const linkProductToQuickbooksMutation = useMutation(
    dashboardv99990101Client.postPushProductToQuickbooks
  )

  const quickbooksAccountsOptions = quickbooksAccounts.map(account => ({
    label: account.name ?? account.id,
    value: account.id
  }))

  const { fields, queries } = useForm<{ quickbooksAccountId: string }>({
    value: {
      quickbooksAccountId: ''
    },
    fieldConfiguration: [
      {
        property: 'quickbooksAccountId',
        validation: [required]
      }
    ]
  })

  const linkProductToQuickbooksAccount = async () => {
    const result = await linkProductToQuickbooksMutation.mutateAsync({
      productId,
      accountId: fields.quickbooksAccountId.value
    })

    return result
  }

  const handleSubmit = async (): Promise<boolean> => {
    setIsCreating(true)
    displayNotification('Creating item in QuickBooks...', {
      duration: 30000
    })

    try {
      await linkProductToQuickbooksAccount()
    } catch (e) {
      const parsedError = parseMutationError(e)

      if (
        parsedError?.isValidationError &&
        // We could also use an enum/list of error types instead of matching on `message`.
        // But we would have to match on message *somewhere* with the current response payload.
        parsedError?.message === 'Duplicate Name Exists Error'
      ) {
        displayNotification(`This item already exists in QuickBooks`, {
          type: 'error'
        })
      } else {
        displayNotification(`Could not create item in QuickBooks`, {
          type: 'error'
        })
      }
      setIsCreating(false)
      return false
    }

    displayNotification(`Item created in QuickBooks`, {
      type: 'success'
    })

    setIsCreating(false)
    onConfirm?.()
    return true
  }

  return (
    <SimpleModal
      isOpen
      onClose={onClose}
      data-testid="modal.linkQuickbooksItem"
      title={`Create item in QuickBooks`}
      cancelButtonText="Cancel"
      submitButtonText="Create item"
      submittingButtonText="Creating item..."
      isLoading={isLoading}
      submitDisabled={!queries.isValid || isCreating}
      submitting={isCreating}
      onSubmit={handleSubmit}
      minHeight="0px"
    >
      <Flex
        color={GreyGrey80}
        {...Primary13Regular}
        flexDirection="column"
        gap="16px"
      >
        <InputSelectControlField
          {...fields.quickbooksAccountId}
          label="Account code"
          data-testid="linkToAccount"
          placeholder="Select an account code..."
          options={quickbooksAccountsOptions}
          disabled={isRefreshing}
        />

        <Flex
          justify="space-between"
          alignContent="center"
          alignItems="center"
          width="100%"
          mt="-16px"
        >
          <Text color={GreyGrey60} {...Primary13Regular}>
            Account not showing up?
          </Text>
          <Flex>
            <Button
              data-testid="refreshAccounts"
              style={{
                height: '32px',
                width: '100%'
              }}
              variant="unstyled"
              size="sm"
              onClick={async () => {
                setIsRefreshing(true)

                await syncQuickbooksAccountsMutator.mutateAsync()

                setTimeout(() => {
                  setIsRefreshing(false)
                }, 1000)
              }}
              aria-label="Refresh accounts"
              disabled={isRefreshing}
              leadingIcon={<RefreshAccountsIcon isRefreshing={isRefreshing} />}
            >
              <Text {...Primary13Regular} color={GreyGrey60}>
                Trigger a refresh
              </Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </SimpleModal>
  )
}
